import React, {useState, useEffect, useCallback, useRef} from 'react';
import {useSelector} from 'react-redux';
import PropTypes from 'prop-types';
import CreateSignupApi from '../../api/createSignup.api';
import {getBearerHeader} from '../../helpers/api.helper';
import Loading from '../../components/Loading/Loading';
import PopupModal from '../../components/PopupModal/PopupModal';
import {
  PageWrapper,
  PageTitle,
  StepIndicatorWrapper,
  StepItem,
  StepDot,
  StepLabel,
  StepLine,
  LocationBanner,
  StepCard,
  SectionTitle,
  DropZone,
  DropZoneLabel,
  PhotoGrid,
  PhotoCard,
  PhotoThumb,
  DeleteButton,
  FormGroup,
  Label,
  Input,
  FieldError,
  ResolvedFieldsGrid,
  ResolvedFieldCard,
  FieldLabel,
  FieldValueRow,
  FieldValue,
  FieldNullValue,
  FieldSource,
  EditButton,
  InlineEditRow,
  SaveButton,
  CancelEditButton,
  ProcessingWrapper,
  ProcessingTitle,
  ProcessingStatusText,
  SpinnerWrapper,
  StatusBadge,
  ErrorMessage,
  SuccessCard,
  ButtonRow,
  PrimaryButton,
  SecondaryButton,
  LoadingOverlay,
  CenteredSection,
  CameraOverlay,
  VideoPreview,
  CaptureButton,
  CameraControls,
  SectionHint,
  PhotoPreviewOverlay,
} from './CreateSignupPage.style';

const ACCEPTED_TYPES = ['image/jpeg', 'image/png'];
const ACCEPTED_EXTENSIONS = 'image/*';

const STEP_LABELS = ['Upload Photo', 'Contact Infomation', 'Processing'];

const TERMINAL_STATUSES = ['VERIFIED', 'FAILED', 'ACCOUNT_CREATED'];

const INTERMEDIATE_STATUS_LABELS = {
  CREATED: 'Signup created',
  DOCUMENT_UPLOADED: 'Documents received',
  EXTRACTING: 'Extracting document data…',
  EXTRACTED: 'Data extracted',
  VERIFYING: 'Verifying business details…',
  SUBMITTING: 'Submitting to onboarding…',
};

const RESOLVED_FIELD_LABELS = {
  businessName: 'Business Name',
  tradingName: 'Trading Name',
  vatNumber: 'VAT Number',
  companyNumber: 'Company Number',
  streetAddress: 'Street Address',
  city: 'City',
  postalCode: 'Postcode',
  phoneNumber: 'Phone Number',
  email: 'Email',
  website: 'Website',
  contactName: 'Contact Name',
  customerType: 'Customer Type',
};

// ─── Step Indicator ───────────────────────────────────────────────────────────

const StepIndicator = ({currentStep}) => (
  <StepIndicatorWrapper>
    {STEP_LABELS.map((label, i) => (
      <React.Fragment key={i}>
        <StepItem>
          <StepDot $active={currentStep === i} $done={currentStep > i}>
            {currentStep > i ? '✓' : i + 1}
          </StepDot>
          <StepLabel $active={currentStep === i}>{label}</StepLabel>
        </StepItem>
        {i < STEP_LABELS.length - 1 && <StepLine $done={currentStep > i} />}
      </React.Fragment>
    ))}
  </StepIndicatorWrapper>
);

StepIndicator.propTypes = {currentStep: PropTypes.number.isRequired};

// ─── Resolved Field Row ───────────────────────────────────────────────────────

const ResolvedField = ({fieldName, fieldData, onSave, saving, saveError}) => {
  const [editing, setEditing] = useState(false);
  const [editVal, setEditVal] = useState('');
  const inputRef = useRef(null);

  const startEdit = () => {
    setEditVal(fieldData ? fieldData.value || '' : '');
    setEditing(true);
    setTimeout(() => inputRef.current && inputRef.current.focus(), 0);
  };

  const cancelEdit = () => {
    setEditing(false);
    setEditVal('');
  };

  const handleSave = async () => {
    await onSave(fieldName, editVal);
    setEditing(false);
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter') handleSave();
    if (e.key === 'Escape') cancelEdit();
  };

  const label = RESOLVED_FIELD_LABELS[fieldName] || fieldName;
  const currentValue = fieldData ? fieldData.value : null;
  const source = fieldData ? fieldData.source : null;

  return (
    <ResolvedFieldCard>
      <FieldLabel>{label}</FieldLabel>
      {editing ? (
        <>
          <InlineEditRow>
            <input
              ref={inputRef}
              value={editVal}
              onChange={e => setEditVal(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={saving}
            />
            <SaveButton onClick={handleSave} disabled={saving}>
              {saving ? '…' : 'Save'}
            </SaveButton>
            <CancelEditButton onClick={cancelEdit} disabled={saving}>
              ✕
            </CancelEditButton>
          </InlineEditRow>
          {saveError && <FieldError>{saveError}</FieldError>}
        </>
      ) : (
        <FieldValueRow>
          {currentValue != null ? (
            <FieldValue>{currentValue}</FieldValue>
          ) : (
            <FieldNullValue>Not available</FieldNullValue>
          )}
          {source && <FieldSource>{source}</FieldSource>}
          <EditButton onClick={startEdit}>Edit</EditButton>
        </FieldValueRow>
      )}
    </ResolvedFieldCard>
  );
};

ResolvedField.propTypes = {
  fieldName: PropTypes.string.isRequired,
  fieldData: PropTypes.object,
  onSave: PropTypes.func.isRequired,
  saving: PropTypes.bool,
  saveError: PropTypes.string,
};

// ─── Main Page ────────────────────────────────────────────────────────────────

const CreateSignupPage = () => {
  const auth = useSelector(state => state.auth);
  const authHeaders = getBearerHeader(null, auth);

  // Location + init
  const [locationStatus, setLocationStatus] = useState('idle');
  const [location, setLocation] = useState(null);
  const [signupId, setSignupId] = useState(null);
  const [initLoading, setInitLoading] = useState(false);
  const [initError, setInitError] = useState(null);

  // Step navigation
  const [currentStep, setCurrentStep] = useState(0);

  // Step 1 — Upload
  const [pendingPhotos, setPendingPhotos] = useState([]); // staged locally, not yet uploaded
  const [uploadedDocs, setUploadedDocs] = useState([]);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraPermission, setCameraPermission] = useState('idle');
  const [cameraStream, setCameraStream] = useState(null);
  const [cameraMirrored, setCameraMirrored] = useState(false);
  const [previewPhoto, setPreviewPhoto] = useState(null);
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  // Step 2 — Contact Infomation + resolved data
  const [email, setEmail] = useState('');
  const [confirmEmail, setConfirmEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [emailError, setEmailError] = useState(null);
  const [confirmEmailError, setConfirmEmailError] = useState(null);
  const [mobileError, setMobileError] = useState(null);
  const [savingContact, setSavingContact] = useState(false);
  const [resolvedData, setResolvedData] = useState(null);
  const [fieldSaving, setFieldSaving] = useState({});
  const [fieldSaveErrors, setFieldSaveErrors] = useState({});

  // Step 3 — Processing
  const [processingStatus, setProcessingStatus] = useState('idle');
  const [processingError, setProcessingError] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [accountResult, setAccountResult] = useState(null);
  const [confirmSubmitOpen, setConfirmSubmitOpen] = useState(false);

  // ─── Auth guard ─────────────────────────────────────────────────────────────

  const isLoggedIn = !!authHeaders;

  // ─── Location helpers ────────────────────────────────────────────────────────

  const initSignup = useCallback(
    async (lat, lng) => {
      setInitLoading(true);
      setInitError(null);
      try {
        const result = await CreateSignupApi.initSignup(lat, lng, authHeaders);
        setSignupId(result.id);
      } catch (err) {
        setInitError(
          (err.response && err.response.data && err.response.data.message) ||
            err.message ||
            'Failed to start signup session.'
        );
      } finally {
        setInitLoading(false);
      }
    },
    [authHeaders]
  );

  const requestLocation = useCallback(() => {
    if (!isLoggedIn) return;
    setLocationStatus('requesting');
    navigator.geolocation.getCurrentPosition(
      pos => {
        const coords = {lat: pos.coords.latitude, lng: pos.coords.longitude};
        setLocation(coords);
        setLocationStatus('granted');
        initSignup(coords.lat, coords.lng);
      },
      () => {
        setLocationStatus('denied');
      },
      {timeout: 10000}
    );
  }, [isLoggedIn, initSignup]);

  useEffect(() => {
    if (isLoggedIn && locationStatus === 'idle') {
      requestLocation();
    }
  }, [isLoggedIn]); // intentionally omit requestLocation — calling once on login is correct

  // ─── Step 1: Photo capture ────────────────────────────────────────────────────

  const stagePhoto = useCallback(files => {
    const valid = Array.from(files).filter(f =>
      ACCEPTED_TYPES.includes(f.type)
    );
    if (valid.length === 0) {
      setUploadError('Please take or select a JPEG or PNG photo.');
      return;
    }
    setUploadError(null);
    setPendingPhotos(prev => [
      ...prev,
      ...valid.map(f => ({file: f, preview: URL.createObjectURL(f)})),
    ]);
  }, []);

  const removePendingPhoto = useCallback(index => {
    setPendingPhotos(prev => {
      URL.revokeObjectURL(prev[index].preview);
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  const handleDrop = useCallback(
    e => {
      e.preventDefault();
      setIsDragOver(false);
      stagePhoto(e.dataTransfer.files);
    },
    [stagePhoto]
  );

  const handleDragOver = useCallback(e => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => setIsDragOver(false), []);

  const handleFileInput = useCallback(
    e => {
      stagePhoto(e.target.files);
      e.target.value = '';
    },
    [stagePhoto]
  );

  const handleDeleteDoc = useCallback(
    async (docId, docName) => {
      try {
        await CreateSignupApi.deleteDocument(signupId, docId, authHeaders);
        setUploadedDocs(prev => {
          const removed = prev.find(d => d.id === docId);
          if (removed && removed.preview) URL.revokeObjectURL(removed.preview);
          return prev.filter(d => d.id !== docId);
        });
      } catch (err) {
        setUploadError(`Failed to remove "${docName}". Please try again.`);
      }
    },
    [signupId, authHeaders]
  );

  const handleNextFromStep1 = useCallback(async () => {
    if (pendingPhotos.length === 0 && uploadedDocs.length === 0) return;

    if (pendingPhotos.length > 0) {
      setUploadLoading(true);
      setUploadError(null);
      try {
        const files = pendingPhotos.map(p => p.file);
        const result = await CreateSignupApi.uploadDocuments(
          signupId,
          files,
          location,
          authHeaders
        );
        const newDocs = (result.documentIds || []).map((id, i) => ({
          id,
          name: `Photo ${uploadedDocs.length + i + 1}`,
          preview: pendingPhotos[i] ? pendingPhotos[i].preview : null,
        }));
        setUploadedDocs(prev => [...prev, ...newDocs]);
        setPendingPhotos([]);
      } catch (err) {
        setUploadError(
          (err.response && err.response.data && err.response.data.message) ||
            err.message ||
            'Upload failed. Please try again.'
        );
        setUploadLoading(false);
        return;
      }
      setUploadLoading(false);
    }

    setCurrentStep(1);
  }, [pendingPhotos, uploadedDocs.length, signupId, location, authHeaders]);

  // ─── Step 1: Camera ───────────────────────────────────────────────────────────

  // Attach stream to video element whenever the camera overlay opens
  useEffect(() => {
    if (cameraOpen && cameraStream && videoRef.current) {
      videoRef.current.srcObject = cameraStream;
      videoRef.current.play().catch(() => {});
    }
  }, [cameraOpen, cameraStream]);

  // Stop camera tracks when stream changes or component unmounts
  useEffect(() => {
    return () => {
      if (cameraStream) {
        cameraStream.getTracks().forEach(t => t.stop());
      }
    };
  }, [cameraStream]);

  const openCamera = useCallback(async () => {
    // getUserMedia unavailable (HTTP, old browser) — fall back to file picker
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      fileInputRef.current && fileInputRef.current.click();
      return;
    }
    setCameraPermission('requesting');
    setUploadError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {facingMode: {ideal: 'environment'}},
        audio: false,
      });
      setCameraStream(stream);
      setCameraPermission('granted');
      setCameraOpen(true);
    } catch (err) {
      if (
        err.name === 'NotAllowedError' ||
        err.name === 'PermissionDeniedError'
      ) {
        setCameraPermission('denied');
      } else {
        // Device/hardware error — fall back to file picker
        setCameraPermission('idle');
        fileInputRef.current && fileInputRef.current.click();
      }
    }
  }, []);

  const closeCamera = useCallback(() => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(t => t.stop());
      setCameraStream(null);
    }
    setCameraOpen(false);
    setCameraPermission('idle');
  }, [cameraStream]);

  const capturePhoto = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const MAX_DIM = 1920;
    const scale = Math.min(
      1,
      MAX_DIM / Math.max(video.videoWidth, video.videoHeight)
    );
    canvas.width = Math.round(video.videoWidth * scale);
    canvas.height = Math.round(video.videoHeight * scale);
    const ctx = canvas.getContext('2d');
    if (cameraMirrored) {
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
    }
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(
      blob => {
        if (blob) {
          const file = new File([blob], `photo-${Date.now()}.jpg`, {
            type: 'image/jpeg',
          });
          setPendingPhotos(prev => [
            ...prev,
            {file, preview: URL.createObjectURL(blob)},
          ]);
        }
        closeCamera();
      },
      'image/jpeg',
      0.78
    );
  }, [closeCamera, cameraMirrored]);

  // ─── Step 2: Contact Infomation + field editing ──────────────────────────────────────

  const saveContactField = useCallback(
    async (fieldName, value, setError) => {
      if (!value.trim()) return true;
      try {
        const updated = await CreateSignupApi.updateField(
          signupId,
          fieldName,
          value.trim(),
          authHeaders
        );
        if (updated && updated.resolvedData) {
          setResolvedData(updated.resolvedData);
        }
        return true;
      } catch (err) {
        if (err.response && err.response.status === 409) {
          setError(
            `This ${
              fieldName === 'email' ? 'email address' : 'phone number'
            } is already registered. Please use a different value.`
          );
        } else {
          setError(
            (err.response && err.response.data && err.response.data.message) ||
              err.message ||
              'Failed to save. Please try again.'
          );
        }
        return false;
      }
    },
    [signupId, authHeaders]
  );

  const startProcessing = useCallback(async () => {
    setEmailError(null);
    setConfirmEmailError(null);
    setMobileError(null);

    // Validate email + confirm email before hitting the API
    const emailVal = email.trim();
    const confirmVal = confirmEmail.trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (emailVal && !emailRegex.test(emailVal)) {
      setEmailError('Please enter a valid email address.');
      return;
    }
    if (!confirmVal) {
      setConfirmEmailError('Please confirm the customer email address.');
      return;
    }
    if (!emailRegex.test(confirmVal)) {
      setConfirmEmailError('Please enter a valid email address.');
      return;
    }
    if (emailVal !== confirmVal) {
      setConfirmEmailError('Email addresses do not match.');
      return;
    }

    setSavingContact(true);
    let ok = true;
    if (emailVal) {
      ok = await saveContactField('email', emailVal, setEmailError);
    }
    if (ok && mobile.trim()) {
      ok = await saveContactField('phoneNumber', mobile, setMobileError);
    }

    setSavingContact(false);
    if (ok) setCurrentStep(2);
  }, [email, confirmEmail, mobile, saveContactField]);

  const handleFieldSave = useCallback(
    async (fieldName, value) => {
      setFieldSaving(prev => ({...prev, [fieldName]: true}));
      setFieldSaveErrors(prev => ({...prev, [fieldName]: null}));
      try {
        const updated = await CreateSignupApi.updateField(
          signupId,
          fieldName,
          value,
          authHeaders
        );
        if (updated && updated.resolvedData) {
          setResolvedData(updated.resolvedData);
        }
      } catch (err) {
        let msg =
          (err.response && err.response.data && err.response.data.message) ||
          err.message ||
          'Save failed.';
        if (err.response && err.response.status === 409) {
          msg = 'This value is already in use. Please provide a different one.';
        }
        setFieldSaveErrors(prev => ({...prev, [fieldName]: msg}));
      } finally {
        setFieldSaving(prev => ({...prev, [fieldName]: false}));
      }
    },
    [signupId, authHeaders]
  );

  // ─── Step 3: Polling ──────────────────────────────────────────────────────────

  useEffect(() => {
    if (currentStep !== 2 || !signupId || !authHeaders) return;

    let stopped = false;
    let timeoutId;

    setProcessingStatus('polling');
    setProcessingError(null);

    const poll = async () => {
      if (stopped) return;
      try {
        const data = await CreateSignupApi.getSignup(signupId, authHeaders);
        if (stopped) return;
        setProcessingStatus(data.status);

        if (data.status === 'VERIFIED') {
          setResolvedData(data.resolvedData || {});
          // Pre-fill email/mobile if not yet set
          if (!email && data.resolvedData && data.resolvedData.email) {
            setEmail(data.resolvedData.email.value || '');
          }
          if (!mobile && data.resolvedData && data.resolvedData.phoneNumber) {
            setMobile(data.resolvedData.phoneNumber.value || '');
          }
          // Return to Contact Infomation step for review
          setCurrentStep(1);
          return;
        }

        if (data.status === 'FAILED') {
          setProcessingError(data.errorMessage || 'Verification failed.');
          return;
        }

        if (data.status === 'ACCOUNT_CREATED') {
          return;
        }

        // Intermediate — keep polling
        timeoutId = setTimeout(poll, 3000);
      } catch (err) {
        if (!stopped) {
          setProcessingError(
            (err.response && err.response.data && err.response.data.message) ||
              err.message ||
              'Failed to retrieve status.'
          );
          // Retry after a longer delay on error
          timeoutId = setTimeout(poll, 6000);
        }
      }
    };

    poll();
    return () => {
      stopped = true;
      clearTimeout(timeoutId);
    };
  }, [currentStep, signupId]); // authHeaders stable within session

  // ─── Submit ───────────────────────────────────────────────────────────────────

  const handleSubmit = useCallback(async () => {
    setSubmitLoading(true);
    setSubmitError(null);
    try {
      const result = await CreateSignupApi.submitSignup(signupId, authHeaders);
      if (result.status === 'ACCOUNT_CREATED') {
        setAccountResult(result);
        setProcessingStatus('ACCOUNT_CREATED');
        setCurrentStep(2);
      } else if (result.status === 'FAILED') {
        setSubmitError(result.error || 'Submission failed. Please try again.');
      }
    } catch (err) {
      setSubmitError(
        (err.response && err.response.data && err.response.data.message) ||
          err.message ||
          'Submission failed. Please try again.'
      );
    } finally {
      setSubmitLoading(false);
    }
  }, [signupId, authHeaders]);

  // ─── Create Another ───────────────────────────────────────────────────────────

  const handleCreateAnother = useCallback(async () => {
    setSignupId(null);
    setPendingPhotos(prev => {
      prev.forEach(p => URL.revokeObjectURL(p.preview));
      return [];
    });
    setUploadedDocs(prev => {
      prev.forEach(d => d.preview && URL.revokeObjectURL(d.preview));
      return [];
    });
    setUploadError(null);
    setEmail('');
    setConfirmEmail('');
    setMobile('');
    setEmailError(null);
    setConfirmEmailError(null);
    setMobileError(null);
    setResolvedData(null);
    setFieldSaving({});
    setFieldSaveErrors({});
    setProcessingStatus('idle');
    setProcessingError(null);
    setSubmitError(null);
    setAccountResult(null);
    setCurrentStep(0);

    // location already available — skip permission prompt
    if (location) {
      setInitLoading(true);
      setInitError(null);
      try {
        const result = await CreateSignupApi.initSignup(
          location.lat,
          location.lng,
          authHeaders
        );
        setSignupId(result.id);
      } catch (err) {
        setInitError(
          (err.response && err.response.data && err.response.data.message) ||
            err.message ||
            'Failed to start signup session.'
        );
      } finally {
        setInitLoading(false);
      }
    }
  }, [location, authHeaders]);

  // ─── Render helpers ───────────────────────────────────────────────────────────

  const renderStep1 = () => (
    <StepCard>
      {uploadLoading && (
        <LoadingOverlay>
          <Loading isLight={false} />
          <p>Uploading…</p>
        </LoadingOverlay>
      )}
      <SectionTitle>Upload Photo</SectionTitle>

      <DropZone
        $isDragOver={isDragOver}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={openCamera}
      >
        <DropZoneLabel>📷 Tap to take a photo</DropZoneLabel>
        {/* <p>Or drag &amp; drop an image here</p> */}
        {/* Hidden fallback for browsers without getUserMedia */}
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED_EXTENSIONS}
          multiple
          onChange={handleFileInput}
          style={{display: 'none'}}
        />
        {/* Off-screen canvas used for capturing video frames */}
        <canvas ref={canvasRef} style={{display: 'none'}} />
      </DropZone>

      {cameraPermission === 'denied' && (
        <ErrorMessage>
          Camera access was denied. Please allow camera access in your browser
          settings, then tap the button again.
        </ErrorMessage>
      )}
      {cameraPermission === 'requesting' && (
        <ErrorMessage
          style={{
            color: '#1976d2',
            borderColor: '#1976d2',
            backgroundColor: 'rgba(25,118,210,0.06)',
          }}
        >
          Requesting camera access…
        </ErrorMessage>
      )}

      {pendingPhotos.length > 0 && (
        <PhotoGrid>
          {pendingPhotos.map((photo, i) => (
            <PhotoCard key={i}>
              <PhotoThumb
                src={photo.preview}
                alt={`Photo ${i + 1}`}
                onClick={() => setPreviewPhoto(photo.preview)}
                style={{cursor: 'pointer'}}
              />
              <DeleteButton
                onClick={() => removePendingPhoto(i)}
                title="Remove photo"
                disabled={uploadLoading}
              >
                &times;
              </DeleteButton>
            </PhotoCard>
          ))}
        </PhotoGrid>
      )}

      {uploadedDocs.length > 0 && (
        <PhotoGrid>
          {uploadedDocs.map(doc => (
            <PhotoCard key={doc.id}>
              <PhotoThumb
                src={doc.preview}
                alt={doc.name}
                onClick={() => doc.preview && setPreviewPhoto(doc.preview)}
                style={{cursor: doc.preview ? 'pointer' : 'default'}}
              />
              <DeleteButton
                onClick={() => handleDeleteDoc(doc.id, doc.name)}
                title="Remove photo"
                disabled={uploadLoading}
              >
                &times;
              </DeleteButton>
            </PhotoCard>
          ))}
        </PhotoGrid>
      )}

      {uploadError && <ErrorMessage>{uploadError}</ErrorMessage>}

      <ButtonRow>
        <PrimaryButton
          onClick={handleNextFromStep1}
          disabled={
            (pendingPhotos.length === 0 && uploadedDocs.length === 0) ||
            uploadLoading
          }
        >
          Next
        </PrimaryButton>
      </ButtonRow>
    </StepCard>
  );

  const renderStep2 = () => {
    const isVerified = processingStatus === 'VERIFIED';
    const fieldNames = Object.keys(RESOLVED_FIELD_LABELS);

    return (
      <StepCard>
        {savingContact && (
          <LoadingOverlay>
            <Loading isLight={false} />
            <p>Saving…</p>
          </LoadingOverlay>
        )}
        <SectionTitle>Contact Infomation</SectionTitle>

        <FormGroup>
          <Label htmlFor="signup-email">
            Customer Email <span>(Obligatory)</span>
          </Label>
          <Input
            id="signup-email"
            type="email"
            value={email}
            onChange={e => {
              setEmail(e.target.value);
              setEmailError(null);
              setConfirmEmailError(null);
            }}
            placeholder="email@example.com"
            $hasError={!!emailError}
            disabled={savingContact}
          />
          {emailError && <FieldError>{emailError}</FieldError>}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="signup-confirm-email">
            Confirm Customer Email <span>(Obligatory)</span>
          </Label>
          <Input
            id="signup-confirm-email"
            type="email"
            value={confirmEmail}
            onChange={e => {
              setConfirmEmail(e.target.value);
              setConfirmEmailError(null);
            }}
            placeholder="Please confirm the email"
            $hasError={!!confirmEmailError}
            disabled={savingContact}
            onPaste={e => e.preventDefault()}
          />
          {confirmEmailError && <FieldError>{confirmEmailError}</FieldError>}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="signup-mobile">
            Customer Mobile <span>(Obligatory)</span>
          </Label>
          <Input
            id="signup-mobile"
            type="tel"
            value={mobile}
            onChange={e => {
              setMobile(e.target.value);
              setMobileError(null);
            }}
            placeholder="+44 7700 000000"
            $hasError={!!mobileError}
            disabled={savingContact}
          />
          {mobileError && <FieldError>{mobileError}</FieldError>}
        </FormGroup>

        {isVerified && resolvedData && (
          <>
            <SectionTitle style={{marginTop: '2.4rem'}}>
              Extracted Business Data
            </SectionTitle>
            <SectionHint>
              Double check and edit any missing/incorrect information. Make sure
              information is correct, especially the email.
            </SectionHint>
            <ResolvedFieldsGrid>
              {fieldNames.map(name => (
                <ResolvedField
                  key={name}
                  fieldName={name}
                  fieldData={resolvedData[name]}
                  onSave={handleFieldSave}
                  saving={!!fieldSaving[name]}
                  saveError={fieldSaveErrors[name]}
                />
              ))}
            </ResolvedFieldsGrid>

            {submitError && (
              <ErrorMessage style={{marginTop: '2rem'}}>
                {submitError}
              </ErrorMessage>
            )}

            <ButtonRow>
              <SecondaryButton
                onClick={() => setCurrentStep(0)}
                disabled={submitLoading}
              >
                Back to Upload Photo
              </SecondaryButton>
              <PrimaryButton
                onClick={() => setConfirmSubmitOpen(true)}
                disabled={submitLoading}
              >
                {submitLoading ? 'Submitting…' : 'Submit'}
              </PrimaryButton>
            </ButtonRow>
          </>
        )}

        {!isVerified && (
          <ButtonRow>
            <SecondaryButton onClick={() => setCurrentStep(0)}>
              Back
            </SecondaryButton>
            <PrimaryButton onClick={startProcessing} disabled={savingContact}>
              Start Processing
            </PrimaryButton>
          </ButtonRow>
        )}
      </StepCard>
    );
  };

  const renderStep3 = () => {
    if (processingStatus === 'ACCOUNT_CREATED' && accountResult) {
      return (
        <SuccessCard>
          <h2>Account Created!</h2>
          <p>
            Trading Account ID:{' '}
            <strong>{accountResult.tradingAccountId || '—'}</strong>
          </p>
          <p>Signup ID: {accountResult.signupId}</p>
          <ButtonRow style={{justifyContent: 'center', marginTop: '2.4rem'}}>
            <PrimaryButton onClick={handleCreateAnother}>
              Create Another Signup
            </PrimaryButton>
          </ButtonRow>
        </SuccessCard>
      );
    }

    if (processingStatus === 'FAILED') {
      return (
        <StepCard>
          <ProcessingWrapper>
            <StatusBadge $status="FAILED">FAILED</StatusBadge>
            <ProcessingTitle style={{marginTop: '1.6rem'}}>
              Verification Failed
            </ProcessingTitle>
            <ProcessingStatusText>
              {processingError || 'The document could not be verified.'}
            </ProcessingStatusText>
          </ProcessingWrapper>
          <ButtonRow style={{justifyContent: 'center'}}>
            <SecondaryButton onClick={() => setCurrentStep(0)}>
              Back to Documents
            </SecondaryButton>
          </ButtonRow>
        </StepCard>
      );
    }

    const statusLabel =
      INTERMEDIATE_STATUS_LABELS[processingStatus] || 'Processing…';

    return (
      <StepCard>
        <ProcessingWrapper>
          <ProcessingTitle>Processing Your Signup</ProcessingTitle>
          <SpinnerWrapper>
            <Loading isLight={false} />
          </SpinnerWrapper>
          <ProcessingStatusText>{statusLabel}</ProcessingStatusText>
          {processingStatus && processingStatus !== 'polling' && (
            <StatusBadge
              $status={processingStatus}
              style={{marginTop: '1.2rem'}}
            >
              {processingStatus.replace(/_/g, ' ')}
            </StatusBadge>
          )}
        </ProcessingWrapper>
        {processingError && (
          <ErrorMessage style={{textAlign: 'center'}}>
            {processingError}
          </ErrorMessage>
        )}
      </StepCard>
    );
  };

  // ─── Pre-signup screens ───────────────────────────────────────────────────────
  const pageTitle = 'New Customer Registration';
  if (!isLoggedIn) {
    return (
      <PageWrapper>
        <PageTitle>{pageTitle}</PageTitle>
        <CenteredSection>
          <p>Please log in to create a new signup.</p>
        </CenteredSection>
      </PageWrapper>
    );
  }

  if (locationStatus === 'denied') {
    return (
      <PageWrapper>
        <PageTitle>{pageTitle}</PageTitle>
        <LocationBanner>
          <p>
            Location access is required to create a signup. Please enable
            location permissions in your browser and try again.
          </p>
          <PrimaryButton onClick={requestLocation}>
            Use My Location
          </PrimaryButton>
        </LocationBanner>
      </PageWrapper>
    );
  }

  if (
    initLoading ||
    locationStatus === 'requesting' ||
    locationStatus === 'idle'
  ) {
    return (
      <PageWrapper>
        <PageTitle>{pageTitle}</PageTitle>
        <CenteredSection>
          <Loading isLight={false} />
          <p>
            {locationStatus === 'requesting'
              ? 'Requesting location…'
              : 'Starting session…'}
          </p>
        </CenteredSection>
      </PageWrapper>
    );
  }

  if (initError) {
    return (
      <PageWrapper>
        <PageTitle>{pageTitle}</PageTitle>
        <ErrorMessage>{initError}</ErrorMessage>
        <ButtonRow>
          <PrimaryButton onClick={requestLocation}>Retry</PrimaryButton>
        </ButtonRow>
      </PageWrapper>
    );
  }

  // ─── Main render ──────────────────────────────────────────────────────────────

  return (
    <>
      {cameraOpen && (
        <CameraOverlay>
          <VideoPreview
            ref={videoRef}
            autoPlay
            playsInline
            muted
            style={cameraMirrored ? {transform: 'scaleX(-1)'} : undefined}
          />
          <CameraControls>
            <SecondaryButton
              onClick={() => setCameraMirrored(m => !m)}
              style={{color: '#fff', borderColor: 'rgba(255,255,255,0.5)'}}
              title="Mirror image"
            >
              {cameraMirrored ? '↔ Mirrored' : '↔ Mirror'}
            </SecondaryButton>
            <CaptureButton onClick={capturePhoto} title="Capture photo" />
            <SecondaryButton
              onClick={closeCamera}
              style={{color: '#fff', borderColor: 'rgba(255,255,255,0.5)'}}
            >
              Cancel
            </SecondaryButton>
          </CameraControls>
        </CameraOverlay>
      )}
      {previewPhoto && (
        <PhotoPreviewOverlay onClick={() => setPreviewPhoto(null)}>
          <img src={previewPhoto} alt="Preview" />
        </PhotoPreviewOverlay>
      )}
      <PopupModal
        showModal={confirmSubmitOpen}
        title="Is all the information provided correct?"
        showOKButton
        okButtonText="Yes, submit"
        callback={() => {
          setConfirmSubmitOpen(false);
          handleSubmit();
        }}
        showCancelButton
        cancelButtonText="No, go back"
        cancelCallBack={() => setConfirmSubmitOpen(false)}
        closeModalCallback={() => setConfirmSubmitOpen(false)}
      />
      <PageWrapper>
        <PageTitle>{pageTitle}</PageTitle>
        <StepIndicator currentStep={currentStep} />
        {currentStep === 0 && renderStep1()}
        {currentStep === 1 && renderStep2()}
        {currentStep === 2 && renderStep3()}
      </PageWrapper>
    </>
  );
};

CreateSignupPage.propTypes = {};

export default CreateSignupPage;
